// #12 - "Unfinished Signings" resource for the Applied GA admin dashboard.
// Contract requests that started DocuSign signing but never finished (the agent
// dropped off). Admins can see who they are and email a reminder to finish.
// Applied GA admins only. See app/controllers/unsigned-contract-request.controller.js.
import { useState } from "react";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  SearchInput,
  Show,
  SimpleShowLayout,
  TextField,
  useGetList,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useAuth } from "../../../hooks/useAuth";
import { paymentLabels } from "../../../lib/constants";
import docusignService from "../../../services/docusign.service";

const fullName = (record) =>
  [record?.payload?.firstName, record?.payload?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() || "(no name)";

const statusLabels = {
  sent: "Sent (awaiting signature)",
  delivered: "Opened",
  completed: "Signed (not submitted)",
  declined: "Cancelled by agent",
  voided: "Voided",
};

// #41 - two kinds of unfinished DocuSign live in this queue. A "held" record is
// one the agent already signed and submitted; it is now waiting on a principal at
// the agency to countersign the LOA/AOC. Everything else is an agent drop-off.
const isHeld = (record) => !!record?.heldContractRequest;
const waitingOn = (record) => (isHeld(record) ? "Principal (AOC countersignature)" : "Agent");
// #115 - the row's agent already has a later submitted request, so this signed
// attempt was almost certainly abandoned and replaced. Server sets
// supersededByRequest to the replacing request's id.
const isSuperseded = (record) => !!record?.supersededByRequest;

const statusFor = (record) => {
  if (isHeld(record)) return "Signed by agent, awaiting principal countersignature";
  // #115 - fold the superseded signal into the status rather than a separate
  // column: a signed attempt the agent replaced with a later submitted request.
  if (isSuperseded(record)) return "Signed, likely superseded";
  return statusLabels[record.envelopeStatus] || record.envelopeStatus;
};

// #12 - admin action: email the agent a link to finish signing. Each send is
// recorded on the record (shown in the reminder history below).
const SendReminderButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  if (!record) return null;

  const onClick = () => {
    setBusy(true);
    docusignService
      .sendReminder(record.id)
      .then(() => {
        notify("Reminder sent", { type: "success" });
        refresh();
      })
      .catch(() => notify("Could not send reminder", { type: "warning" }))
      .finally(() => setBusy(false));
  };

  const held = isHeld(record);
  const label = held ? "Remind principal/agency" : "Remind agent";
  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {held
          ? "Signed by the agent, waiting on a principal at the agency. The reminder emails the agency's principal accounts a link to countersign."
          : "Waiting on the agent to finish signing. The reminder emails the agent a link to resume."}
      </Typography>
      <Button variant="contained" disabled={busy} onClick={onClick}>
        {busy ? "Sending..." : label}
      </Button>
    </Box>
  );
};

// #12 - the full set of details the agent had entered before they dropped off.
// Everything is stored in `payload`; this surfaces it so an admin reviewing a
// drop-off (or before reminding) can see exactly what was captured. SSN is
// intentionally not shown here.
const row = (label, value) =>
  value === undefined || value === null || value === "" ? null : (
    <Typography variant="body2">
      <strong>{label}:</strong> {value}
    </Typography>
  );

const SubmittedDetails = () => {
  const record = useRecordContext();
  const p = record?.payload || {};
  const carriers = Array.isArray(p.contracts) ? p.contracts : [];
  const address = [p.address, p.city, p.state, p.zip].filter(Boolean).join(", ");
  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Submitted details (captured before drop-off)
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
        {row("Preferred name", p.preferredName)}
        {row("Phone", p.phone)}
        {row("Address", address)}
        {row("Date of birth", p.dob ? new Date(p.dob).toLocaleDateString() : null)}
        {row("Request type", p.requestType)}
        {row("Recommended by (FSD)", p.fsd)}
        {row("Commission email", p.commissionEmail)}
        {row("Enrollment email", p.enrollmentEmail)}
        {row("Payment NPN", p.paymentNpn)}
        {row("Corporation / LLC", p.paymentCorporation)}
        {row("Tax ID", p.paymentTaxId)}
        {row("Upline (LOA)", p.paymentUpline)}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Carriers requested ({carriers.length}):</strong>
          </Typography>
          {carriers.length === 0 ? (
            <Typography variant="body2">None yet.</Typography>
          ) : (
            carriers.map((c, i) => (
              <Typography key={i} variant="body2">
                {c.label || c.value}
                {c.policyCreation ? ` (${c.policyCreation})` : ""}
                {Array.isArray(c.states) && c.states.length ? ` - ${c.states.join(", ")}` : ""}
              </Typography>
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// #12 - reminder history. Shown on the Show page only (not the list).
const ReminderHistory = () => {
  const record = useRecordContext();
  const reminders = record?.reminders || [];
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Reminders sent ({reminders.length})
      </Typography>
      {reminders.length === 0 ? (
        <Typography variant="body2">No reminders sent yet.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
          {reminders.map((r, i) => (
            <Typography key={i} variant="body2">
              {new Date(r.sentAt).toLocaleString()} to {r.to}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
};

// #115 - when a record is likely superseded (the agent has a later submitted
// request), offer the admin a link to that request and an Archive button to
// clear this dead attempt off the list.
const SupersededPanel = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  if (!record || !isSuperseded(record)) return null;

  const onArchive = () => {
    setBusy(true);
    docusignService
      .archive(record.id)
      .then(() => {
        notify("Archived", { type: "success" });
        redirect("list", "unsigned-contract-request");
        refresh();
      })
      .catch(() => notify("Could not archive", { type: "warning" }))
      .finally(() => setBusy(false));
  };

  return (
    <Alert severity="warning" sx={{ my: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        This signing looks superseded: the agent has a later submitted request, so
        they most likely abandoned this attempt and redid it. You can open the
        request that replaced it, then archive this one off the list.
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            redirect("show", "contract-request", record.supersededByRequest)
          }
        >
          Open superseding request
        </Button>
        <Button variant="contained" size="small" disabled={busy} onClick={onArchive}>
          {busy ? "Archiving..." : "Archive this attempt"}
        </Button>
      </Box>
    </Alert>
  );
};

// #84 - free-text search: partial match on the signer's name, email, and NPN.
const unsignedFilters = [
  <SearchInput source="q" placeholder="Name, email, or NPN" alwaysOn />,
];

export const UnsignedContractRequestList = () => (
  <List
    sort={{ field: "createdAt", order: "DESC" }}
    exporter={false}
    title="Unfinished DocuSigns"
    filters={unsignedFilters}
  >
    <>
      <Alert severity="info" sx={{ m: 2 }}>
        These DocuSigns started but aren't finished. Some are waiting on the agent
        to sign; some are signed by the agent and waiting on a principal at the
        agency to countersign (LOA/AOC). The "Waiting on" column shows which, and
        the reminder goes to that party.
      </Alert>
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <DateField source="createdAt" label="Started" showTime />
        <FunctionField label="Name" render={fullName} />
        <TextField source="email" label="Email" />
        <FunctionField label="Waiting on" render={waitingOn} />
        <FunctionField label="Status" render={statusFor} />
      </Datagrid>
    </>
  </List>
);

export const UnsignedContractRequestShow = () => (
  <Show title="Unfinished DocuSign">
    <SimpleShowLayout>
      <FunctionField label="Name" render={fullName} />
      <TextField source="email" label="Email" />
      <FunctionField label="NPN" render={(r) => r?.payload?.npn || "—"} />
      <FunctionField
        label="Payment method"
        render={(r) => paymentLabels[r?.payload?.payment] || r?.payload?.payment || "—"}
      />
      <FunctionField label="Waiting on" render={waitingOn} />
      <FunctionField label="Status" render={statusFor} />
      <DateField source="createdAt" label="Started" showTime />
      <DateField source="updatedAt" label="Last activity" showTime />
      <SupersededPanel />
      <SubmittedDetails />
      <SendReminderButton />
      <ReminderHistory />
    </SimpleShowLayout>
  </Show>
);

// #12 - drop-off warning shown on the contract-request list (the Applied GA
// admin landing page). Surfaces unfinished signings and links to the list.
// Applied GA admins only.
export const UnfinishedSigningsAlert = () => {
  const { user, isFullAdmin } = useAuth();
  const redirect = useRedirect();
  const { total, isLoading } = useGetList(
    "unsigned-contract-request",
    { pagination: { page: 1, perPage: 1 } },
    { enabled: !!user && isFullAdmin(user) }
  );

  if (!user || !isFullAdmin(user) || isLoading || !total) return null;

  return (
    <Alert
      severity="warning"
      sx={{ m: 2 }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => redirect("list", "unsigned-contract-request")}
        >
          Review
        </Button>
      }
    >
      {total} contract request{total === 1 ? "" : "s"} started signing but
      {total === 1 ? " hasn't" : " haven't"} been completed.
    </Alert>
  );
};

export default UnsignedContractRequestList;
